import type { Document, Folder } from "@models";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { getNavigableDocumentLabel } from "./documents";

export type DocumentsPageBreadcrumbItem = {
  folderId?: string | null;
  label: string;
  to?: string;
};

const buildRoute = (
  orgId: string,
  { folderId }: { folderId?: string } = {}
) => {
  const route = fillRouteWithSlugs(ROUTES.DOCUMENTS, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });

  if (!folderId) {
    return route;
  }

  return `${route}?folderId=${folderId}`;
};

const buildFolderBreadcrumbItems = ({
  folderId,
  folders,
  orgId,
}: {
  folderId?: string | null;
  folders: Folder[];
  orgId: string;
}): DocumentsPageBreadcrumbItem[] => {
  // Index folders so each parent lookup is constant-time while walking from the
  // active folder back to the root.
  const foldersById = new Map(folders.map((folder) => [folder.id, folder]));

  const visitedParentFolderIds = new Set<string>();
  const rootToActiveFolderPath: Folder[] = [];
  const getFolder = (folderId?: string | null) =>
    folderId ? foldersById.get(folderId) : undefined;

  let currentFolder = getFolder(folderId);

  while (currentFolder && !visitedParentFolderIds.has(currentFolder.id)) {
    visitedParentFolderIds.add(currentFolder.id);

    // We discover folders from leaf to root, but breadcrumbs render root first.
    // Prepending keeps the final path in display order without a later reverse.
    rootToActiveFolderPath.unshift(currentFolder);
    currentFolder = getFolder(currentFolder.parentFolderId);
  }

  return rootToActiveFolderPath.map((folder) => ({
    folderId: folder.id,
    label: folder.name,
    to: buildRoute(orgId, { folderId: folder.id }),
  }));
};

const buildDocumentBreadcrumbItem = (
  document: Document | null | undefined
): { label: string }[] =>
  document
    ? [
        {
          label: getNavigableDocumentLabel(document),
        },
      ]
    : [];

export const buildDocumentsPageBreadcrumbItems = ({
  activeDocument,
  activeFolderId,
  folders,
  orgId,
}: {
  activeDocument?: Document | null;
  activeFolderId?: string | null;
  folders: Folder[];
  orgId: string;
}): DocumentsPageBreadcrumbItem[] => {
  const folderId =
    (activeDocument ? activeDocument.folderId : activeFolderId) ?? null;

  return [
    {
      folderId: null,
      label: "Documents",
      to: buildRoute(orgId),
    },
    ...buildFolderBreadcrumbItems({ folderId, folders, orgId }),
    ...buildDocumentBreadcrumbItem(activeDocument),
  ];
};
